/**
 * Demo mode identifiers shared across app, admin, and backend surfaces.
 *
 * @remarks
 * Demo mode uses in-memory organisation ids with the prefix `demo-org-`;
 * those identifiers are intentionally not Postgres UUIDs. When a UUID is
 * required for persistence or admin tooling, demo org ids map to the dedicated
 * Demo Usage analytics organisation id.
 *
 * @see docs/features/DEMO_COMPANY_README.md
 */

/**
 * Dedicated organisation id used for storing demo usage analytics.
 *
 * @see docs/features/DEMO_COMPANY_README.md
 */
export const DEMO_USAGE_ORG_ID = "086e06c5-b8b0-4528-9ca0-df22b77f2533";

export const DEMO_ORG_ID_PREFIX = "demo-org-";

export const isDemoOrganisationId = (organisationId: string): boolean =>
  organisationId.startsWith(DEMO_ORG_ID_PREFIX);

/**
 * Demo company scenarios used by customer-app fixtures and admin demo tooling.
 *
 * @remarks
 * These are stable non-UUID organisation ids used by the demo router and
 * fixtures. They are intentionally not real `Organisation.id` values.
 *
 * @see docs/features/DEMO_COMPANY_README.md
 */
export const DEMO_COMPANY_ORG_IDS = [
  "demo-org-harbor",
  "demo-org-summit",
  "demo-org-lumen",
] as const;

export type DemoCompanyOrgId = (typeof DEMO_COMPANY_ORG_IDS)[number];

export const DEMO_COMPANY_NAME_BY_ORG_ID: Record<DemoCompanyOrgId, string> = {
  "demo-org-harbor": "Harbor Peak Telecom",
  "demo-org-summit": "Summit Advisory Collective",
  "demo-org-lumen": "Lumen Field Services",
};

export const formatDemoModeOrganisationLabel = (companyName: string): string =>
  `[DEMO MODE]: ${companyName}`;

/**
 * Stable UUID owners for demo-only Budget Editor grouping snapshots.
 *
 * @remarks
 * The grouping contract deliberately requires an RFC UUID even though demo
 * transport organisations use `demo-org-*` identifiers. These reserved values
 * are local fixture identities only and never identify real organisations.
 */
export const DEMO_BUDGET_EDITOR_GROUPING_DEMO_ORG_IDS = [
  ...DEMO_COMPANY_ORG_IDS,
  "demo-org-cadence",
  "demo-org-lumen-stale",
] as const;
export type DemoBudgetEditorGroupingDemoOrgId =
  (typeof DEMO_BUDGET_EDITOR_GROUPING_DEMO_ORG_IDS)[number];

export const DEMO_BUDGET_EDITOR_GROUPING_ORG_ID_BY_DEMO_ORG_ID: Record<
  DemoBudgetEditorGroupingDemoOrgId,
  string
> = {
  "demo-org-harbor": "00000000-0000-4000-8000-000000000101",
  "demo-org-summit": "00000000-0000-4000-8000-000000000102",
  "demo-org-lumen": "00000000-0000-4000-8000-000000000103",
  "demo-org-cadence": "00000000-0000-4000-8000-000000000104",
  "demo-org-lumen-stale": "00000000-0000-4000-8000-000000000105",
};

const DEMO_BUDGET_EDITOR_GROUPING_OWNER_ENTRIES = Object.entries(
  DEMO_BUDGET_EDITOR_GROUPING_ORG_ID_BY_DEMO_ORG_ID,
) as Array<[DemoBudgetEditorGroupingDemoOrgId, string]>;

export const resolveDemoBudgetEditorGroupingOrganisationId = (
  demoOrganisationId: string,
): string | null =>
  DEMO_BUDGET_EDITOR_GROUPING_OWNER_ENTRIES.find(
    ([candidate]) => candidate === demoOrganisationId,
  )?.[1] ?? null;

export const resolveDemoOrganisationIdFromBudgetEditorGroupingOwner = (
  groupingOrganisationId: string,
): DemoBudgetEditorGroupingDemoOrgId | null =>
  DEMO_BUDGET_EDITOR_GROUPING_OWNER_ENTRIES.find(
    ([, owner]) => owner === groupingOrganisationId.toLowerCase(),
  )?.[0] ?? null;

const DEMO_BUDGET_HISTORY_ITEM_ID_PATTERN =
  /^demo_history_item_([0-9a-f]{4})([0-9a-f]{12})$/;

/**
 * Maps one demo Item identity into the UUID-shaped account identity required
 * by the grouping contract without widening production item-id validation.
 */
export const resolveDemoBudgetEditorGroupingAccountId = (
  groupingOrganisationId: string,
  demoItemId: string,
): string | null => {
  if (
    resolveDemoOrganisationIdFromBudgetEditorGroupingOwner(
      groupingOrganisationId,
    ) === null
  ) {
    return null;
  }
  const match = demoItemId.match(DEMO_BUDGET_HISTORY_ITEM_ID_PATTERN);
  return match ? `00000000-0000-0000-${match[1]}-${match[2]}` : null;
};

/**
 * Bug Tracker demo organisation ids stored as UUID sentinel values.
 *
 * @remarks
 * Legacy Bug Tracker storage uses a UUID `organisation_id`, while demo org ids
 * are non-UUID fixture identifiers. These reserved UUIDs represent each demo
 * company scenario and do not correspond to real `Organisation.id` rows.
 *
 * @see docs/features/BUG_TRACKER.md
 */
export const DEMO_BUG_TRACKER_ORG_IDS = [
  "00000000-0000-0000-0000-000000000001",
  "00000000-0000-0000-0000-000000000002",
  "00000000-0000-0000-0000-000000000003",
] as const;

export type DemoBugTrackerOrgId = (typeof DEMO_BUG_TRACKER_ORG_IDS)[number];

const DEMO_BUG_TRACKER_ORG_ID_SET: ReadonlySet<string> = new Set(
  DEMO_BUG_TRACKER_ORG_IDS,
);

export const isDemoBugTrackerOrganisationId = (
  organisationId: string,
): organisationId is DemoBugTrackerOrgId =>
  DEMO_BUG_TRACKER_ORG_ID_SET.has(organisationId);

export const DEMO_BUG_TRACKER_ORG_ID_BY_DEMO_ORG_ID: Record<
  DemoCompanyOrgId,
  DemoBugTrackerOrgId
> = {
  "demo-org-harbor": "00000000-0000-0000-0000-000000000001",
  "demo-org-summit": "00000000-0000-0000-0000-000000000002",
  "demo-org-lumen": "00000000-0000-0000-0000-000000000003",
};

export interface DemoBugTrackerOrganisation {
  demoOrgId: DemoCompanyOrgId;
  bugTrackerOrganisationId: DemoBugTrackerOrgId;
  companyName: string;
}

export const DEMO_BUG_TRACKER_ORGANISATIONS = [
  {
    demoOrgId: "demo-org-harbor",
    bugTrackerOrganisationId: "00000000-0000-0000-0000-000000000001",
    companyName: "Harbor Peak Telecom",
  },
  {
    demoOrgId: "demo-org-summit",
    bugTrackerOrganisationId: "00000000-0000-0000-0000-000000000002",
    companyName: "Summit Advisory Collective",
  },
  {
    demoOrgId: "demo-org-lumen",
    bugTrackerOrganisationId: "00000000-0000-0000-0000-000000000003",
    companyName: "Lumen Field Services",
  },
] as const satisfies readonly DemoBugTrackerOrganisation[];

/**
 * Resolves the organisation id used for usage tracking persistence.
 *
 * @remarks
 * Demo org ids map to `DEMO_USAGE_ORG_ID`; non-demo ids pass through
 * unchanged.
 *
 * @see docs/features/DEMO_COMPANY_README.md
 */
export const resolveUsageTrackingOrganisationId = (
  organisationId: string,
): string =>
  isDemoOrganisationId(organisationId) ? DEMO_USAGE_ORG_ID : organisationId;
